import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { callAPI } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';

export const createDriver = createAsyncThunk(
  'driver/createDriver',
  async (driverData, { rejectWithValue }) => {
    try {
      const response = await callAPI(apiUrls.createDriver, {}, 'POST', driverData);

      if (response?.data?.success) {
        return response?.data?.data;
      } else {
        return rejectWithValue(response?.data?.message || 'Failed to create driver');
      }
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to create driver');
    }
  }
);

export const updateDriver = createAsyncThunk(
  'driver/updateDriver',
  async ({ id, driverData }, { rejectWithValue }) => {
    try {
      const response = await callAPI(`${apiUrls.updateDriver}/${id}`, {}, 'PATCH', driverData);

      if (response?.data?.success) {
        return response?.data?.data;
      } else {
        return rejectWithValue(response?.data?.message || 'Failed to update driver');
      }
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update driver');
    }
  }
);

export const deleteDriver = createAsyncThunk(
  'driver/deleteDriver',
  async (id, { rejectWithValue }) => {
    try {
      const response = await callAPI(apiUrls.deleteDriver, { _id: id }, 'DELETE', {});

      if (response?.data?.success) {
        return { id, message: response?.data?.message };
      } else {
        return rejectWithValue(response?.data?.message || 'Failed to delete driver');
      }
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to delete driver');
    }
  }
);

export const updateDriverStatus = createAsyncThunk(
  'driver/updateDriverStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await callAPI(apiUrls.driverStatusUpdate, { _id: id, status: status }, 'PATCH', { status });

      if (response?.data?.success) {
        return { id, status, message: response?.data?.message };
      } else {
        return rejectWithValue(response?.data?.message || 'Failed to update driver status');
      }
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update driver status');
    }
  }
);

const initialState = {
  loading: false,
  error: null,
  successMessage: null,
};

const driverSlice = createSlice({
  name: 'driver',
  initialState,
  reducers: {
    clearDriverError: (state) => {
      state.error = null;
    },
    clearDriverSuccess: (state) => {
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createDriver.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createDriver.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = 'Driver created successfully';
        state.error = null;
      })
      .addCase(createDriver.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateDriver.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(updateDriver.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = 'Driver updated successfully';
        state.error = null;
      })
      .addCase(updateDriver.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(deleteDriver.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(deleteDriver.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message || 'Driver deleted successfully';
        state.error = null;
      })
      .addCase(deleteDriver.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateDriverStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(updateDriverStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message || `Status changed to ${action.payload.status}`;
        state.error = null;
      })
      .addCase(updateDriverStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearDriverError, clearDriverSuccess } = driverSlice.actions;
export default driverSlice.reducer;